import React, { useCallback, memo, ReactElement, useRef, useEffect, ReactNode } from 'react'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Skeleton } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { EditIcon } from 'transfix-icons'
import 'scss/5_components/_inline-edit-container.scss'

interface ILoadStateDimensions {
  height: number
  width: number
}

export interface InlineEditContainerProps extends JSX.ElementChildrenAttribute {
  name?: string
  className?: string
  onEditModeRequested(name?: string): void
  editModeEnabled?: boolean
  editMode: ReactElement
  resetOnClickOutside?: boolean
  isLocked?: boolean
  lockedIcon?: ReactNode
  dataTest?: string
  editIconLocation?: 'top' | 'center' | 'bottom'
  isLoading?: boolean
  loadingStateComponentDimensions?: ILoadStateDimensions
}

const DEFAULT_LOAD_STATE_COMPONENT_DIMENSIONS = {
  width: 190,
  height: 20,
}

const noop = () => {}

const CONTAINER_CLASS = 'inline-edit-container'

const hasInlineEditContainerParent = (node: HTMLElement | null): boolean => {
  while (node) {
    if (node.classList.contains(CONTAINER_CLASS)) {
      return true
    }
    node = node.parentElement
  }
  return false
}

export const InlineEditContainer = memo<InlineEditContainerProps>(
  ({
    isLoading = false,
    name,
    onEditModeRequested,
    editModeEnabled,
    children,
    editMode,
    className = '',
    resetOnClickOutside = true,
    dataTest = '',
    isLocked = false,
    lockedIcon,
    editIconLocation = 'center',
    loadingStateComponentDimensions = DEFAULT_LOAD_STATE_COMPONENT_DIMENSIONS,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (editModeEnabled && resetOnClickOutside) {
        const clickOutsideHandler = (e: MouseEvent) => {
          if (!hasInlineEditContainerParent(e.target as HTMLElement)) {
            onEditModeRequested()
          }
        }
        document.addEventListener('mousedown', clickOutsideHandler)

        return () => document.removeEventListener('mousedown', clickOutsideHandler)
      }
      return undefined
    }, [editModeEnabled, resetOnClickOutside])

    const clickHandler = useCallback(() => {
      // prevent edit if isLocked
      if (!isLocked) {
        // allow to select text without firing click
        const selection = global.getSelection()
        const { current: node } = containerRef
        const noSelection =
          node && (!selection || !selection.toString() || !node.contains(selection.anchorNode))
        if (!editModeEnabled && noSelection) {
          onEditModeRequested(name)
        }
      }
    }, [editModeEnabled, isLocked, name, onEditModeRequested])

    const viewModeClasses = isLocked ? 'locked' : 'view-mode'
    const { isMobile } = useBreakpointContext()

    const isIconVisible = isMobile && !isLocked && !editModeEnabled

    const textClickHandler = useCallback(() => {
      if (isMobile) return
      clickHandler()
    }, [clickHandler, isMobile])

    const iconClickHandler = useCallback(() => {
      clickHandler()
    }, [clickHandler])

    const childs = isLoading ? (
      <Skeleton
        variant='rectangular'
        height={loadingStateComponentDimensions.height}
        width={loadingStateComponentDimensions.width}
      />
    ) : (
      children
    )

    return (
      <div
        data-test={dataTest}
        className={`${CONTAINER_CLASS} ${editModeEnabled ? '' : viewModeClasses} ${className}`}
        onClick={textClickHandler}
        onKeyDown={noop}
        ref={containerRef}
        role='button'
        tabIndex={-1}>
        {isLocked && lockedIcon}
        {editModeEnabled ? editMode : childs}
        {isIconVisible && (
          <span className={`inline-edit-icon-wrapper inline-edit-icon-wrapper-${editIconLocation}`}>
            <IconButton
              Icon={EditIcon}
              onClick={iconClickHandler}
              data-test={`${dataTest}-icon`}
              label='Edit'
              size='large'
            />
          </span>
        )}
      </div>
    )
  }
)

export default InlineEditContainer
